import { useMemo, useState } from 'react';
import { DeleteButton, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import {
  DEFAULT_PHASE,
  MA_PHASES,
  MA_WORKSTREAMS,
  phaseInfo,
  type MaToolboxItem,
} from './maToolboxMeta';

/**
 * Acquisition Toolbox — the post-close integration playbook.
 *
 * Ported from the standalone M&A app. Sixty-six seeded items across seven
 * workstreams, each tagged with the phase it belongs to. Tick items off, filter
 * by phase, and add your own.
 */
export function MaToolbox() {
  const items = useResource<MaToolboxItem[]>('/ma/toolbox');
  const [phase, setPhase] = useState<string>('all');
  const [hideDone, setHideDone] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const all = useMemo(() => items.data ?? [], [items.data]);

  const visible = useMemo(
    () => all.filter((i) => (phase === 'all' || i.Phase === phase) && (!hideDone || !i.Checked)),
    [all, phase, hideDone],
  );

  const toggle = async (item: MaToolboxItem) => {
    setProblem(null);
    try {
      await api.put(`/ma/toolbox/${item.MaToolboxItemId}`, { checked: !item.Checked });
      items.reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save');
    }
  };

  const remove = async (id: string) => {
    setProblem(null);
    try {
      await api.del(`/ma/toolbox/${id}`);
      items.reload();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not delete');
    }
  };

  if (items.loading) return <Loading label="Loading the playbook" />;
  if (items.error) return <ErrorBanner message={items.error} />;

  const done = all.filter((i) => i.Checked).length;

  return (
    <div className="stack">
      {problem && <ErrorBanner message={problem} />}

      <div className="card">
        <div className="card-body tight">
          <div className="row wrap">
            <span className="eyebrow">Phase</span>
            <div className="status-group">
              <button
                type="button"
                className={`status-btn${phase === 'all' ? ' met on' : ''}`}
                onClick={() => setPhase('all')}
              >
                All
              </button>
              {MA_PHASES.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`status-btn${phase === p.key ? ' met on' : ''}`}
                  onClick={() => setPhase(p.key)}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <label className="checkbox">
              <input
                type="checkbox"
                checked={hideDone}
                onChange={(e) => setHideDone(e.target.checked)}
              />
              Hide done
            </label>

            <span className="grow" />
            <span className="xs muted">
              <span className="mono">{done}</span> of <span className="mono">{all.length}</span> done
            </span>
          </div>
        </div>
      </div>

      {MA_WORKSTREAMS.map((ws) => {
        const rows = visible.filter((i) => i.Workstream === ws.key);
        const total = all.filter((i) => i.Workstream === ws.key).length;
        const complete = all.filter((i) => i.Workstream === ws.key && i.Checked).length;

        return (
          <div className="card" key={ws.key}>
            <div className="card-head">
              <div className="row-between">
                <div className="grow">
                  <h2>{ws.name}</h2>
                  <p className="sub">{ws.blurb}</p>
                </div>
                <span className="xs muted">
                  <span className="mono">{complete}</span>/<span className="mono">{total}</span>
                </span>
              </div>
            </div>

            <div className="card-body">
              {rows.length === 0 ? (
                <p className="small subtle">
                  {total === 0 ? 'Nothing in this workstream.' : 'Nothing matches the filter.'}
                </p>
              ) : (
                <div className="stack" style={{ gap: 'var(--space-2)' }}>
                  {rows.map((item) => (
                    <ToolboxRow
                      key={item.MaToolboxItemId}
                      item={item}
                      onToggle={() => void toggle(item)}
                      onDelete={() => void remove(item.MaToolboxItemId)}
                    />
                  ))}
                </div>
              )}

              <button
                type="button"
                className="btn btn-ghost btn-sm mt-3"
                onClick={() => setAdding(ws.key)}
              >
                + Add an item
              </button>
            </div>
          </div>
        );
      })}

      {adding && (
        <AddItem
          workstream={adding}
          onClose={() => setAdding(null)}
          onSaved={() => {
            setAdding(null);
            items.reload();
          }}
        />
      )}
    </div>
  );
}

function ToolboxRow({
  item,
  onToggle,
  onDelete,
}: {
  item: MaToolboxItem;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const p = phaseInfo(item.Phase);

  return (
    <div className="ma-item">
      {/* The phase rail carries the urgency, so the row says when as well as
          what without spending a column on it. */}
      <span className="ma-item-rail" style={{ background: p.token }} aria-hidden="true" />

      <label className="checkbox ma-item-check">
        <input type="checkbox" checked={item.Checked} onChange={onToggle} />
      </label>

      <div className="grow">
        <div className={`ma-item-title${item.Checked ? ' done' : ''}`}>{item.Title}</div>
        {item.Detail && <p className="xs muted mt-1">{item.Detail}</p>}
        <span className="xs subtle">{p.label}</span>
        {item.IsCustom && <span className="badge badge-neutral ma-item-tag">Added</span>}
      </div>

      <DeleteButton what={item.Title} onConfirm={onDelete} />
    </div>
  );
}

function AddItem({
  workstream,
  onClose,
  onSaved,
}: {
  workstream: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const ws = MA_WORKSTREAMS.find((w) => w.key === workstream);
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [phase, setPhase] = useState(DEFAULT_PHASE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/ma/toolbox', { workstream, title: title.trim(), detail, phase });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add it');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Add to ${ws?.name ?? 'the playbook'}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Adding…' : 'Add'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="What needs doing">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>

      <Field label="Detail" hint="Why it matters, or what good looks like.">
        <textarea
          className="input"
          rows={3}
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
        />
      </Field>

      <Field label="Phase" hint="When after close this should happen.">
        <select className="select" value={phase} onChange={(e) => setPhase(e.target.value)}>
          {MA_PHASES.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  );
}
